/* ===================================================================
   AddCustomerModal -- add one customer to the signed-in client (Hopper,
   2026-06-17).

   Persists via POST /api/v1/customers (useCustomerMutations.add), scoped
   server-side to the token's client_id. On success the parent invalidates
   the customers query so the new row appears after a real refetch (no local
   mock state). A customer needs at least an email OR a phone (matches the
   server guard + the upsert match key). Nothing here sends any message.
   =================================================================== */
import { useState } from 'react';
import { Icon, ClosePath } from './ui';
import { useCustomerMutations } from '../hooks';

export function AddCustomerModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  /** Fires after a customer is successfully saved (before close). */
  onSaved?: () => void;
}) {
  const { add, isPending, error } = useCustomerMutations();
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const canSave = email.trim() !== '' || phone.trim() !== '';

  async function handleSave() {
    setLocalError(null);
    if (!canSave) {
      setLocalError('Enter at least an email or a phone number.');
      return;
    }
    await add({
      first_name: first.trim(),
      last_name: last.trim(),
      email: email.trim(),
      phone: phone.trim(),
    });
    onSaved?.();
    onClose();
  }

  return (
    <div
      className="modal-bg"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal">
        <div className="modal__head">
          <h3>Add customer</h3>
          <button className="x" onClick={onClose} aria-label="Close">
            <Icon d={ClosePath} />
          </button>
        </div>

        <div className="modal__body">
          <div className="formgrid">
            <label className="fld">
              <span className="lab">First name</span>
              <input
                className="inp"
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                placeholder=""
              />
            </label>
            <label className="fld">
              <span className="lab">Last name</span>
              <input
                className="inp"
                value={last}
                onChange={(e) => setLast(e.target.value)}
                placeholder=""
              />
            </label>
            <label className="fld">
              <span className="lab">Email</span>
              <input
                className="inp"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder=""
              />
            </label>
            <label className="fld">
              <span className="lab">Phone</span>
              <input
                className="inp"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder=""
              />
            </label>
          </div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
            A customer needs at least an email or a phone number.
          </div>
          {(localError || error) && (
            <div
              className="muted"
              style={{ color: 'var(--red, #b5532a)', fontSize: 13, marginTop: 8 }}
            >
              {localError ?? `Could not save${error?.message ? ` (${error.message})` : ''}.`}
            </div>
          )}
        </div>

        <div className="modal__foot">
          <button className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            disabled={!canSave || isPending}
            onClick={handleSave}
          >
            {isPending ? 'Saving...' : 'Save customer'}
          </button>
        </div>
      </div>
    </div>
  );
}
